import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Spinner,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { getStorePacks, createStoreCheckout, type StorePack } from '../api/store.js';
import { useUsageStore, refreshUsageSoon } from '../stores/usageStore.js';
import { kitEquivalenceLine, kitEquivalenceCreditos } from '../lib/planEquivalence.js';
import {
  NOTA_EQUIVALENCIA,
  creditosDeKit,
  enCreditos,
  etiquetaCreditos,
  formatTokens,
  panteonDe,
  panteonValido,
  precioCreditoPanteon,
  precioPanteon,
  promediosDe,
  sugerirPaquete,
} from '../lib/creditos.js';
import { nombreProductoActivo } from '../lib/brandingDelProducto.js';

/**
 * Tienda OPUS ai.
 *  - Con CREDITOS_ENABLED: Recarga 400 (`kit_3m`), Recarga 1,000, 2,500 y 5,000, más la
 *    Recarga a la medida (100–20,000 créditos en pasos de 50, precio por tramos). El precio
 *    de la recarga a la medida que se ve aquí es solo una vista previa: eko27 lo recalcula al crear el pago.
 *  - Apagado: la recarga única de tokens (`kit_3m`) de siempre.
 * Las recargas NO vencen: no se pierden al cambiar de mes ni al terminar el plan; se
 * consumen después de la bolsa mensual (orden real en eko27: bolsa → recargas).
 */

const useStyles = makeStyles({
  surface: {
    width: 'min(520px, calc(100vw - 32px))',
    maxWidth: '520px',
    padding: 0,
    overflow: 'hidden',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-default)',
    boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
  },
  body: { display: 'flex', flexDirection: 'column', gap: 0 },
  hero: {
    position: 'relative',
    padding: '24px 28px 18px',
    background:
      'radial-gradient(120% 80% at 50% -10%, var(--accent-weak) 0%, transparent 55%), linear-gradient(180deg, var(--bg-raised) 0%, var(--bg-surface) 100%)',
    borderBottom: '1px solid var(--border-subtle)',
  },
  eyebrow: {
    display: 'inline-block',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: tokens.colorBrandForeground1,
    marginBottom: '8px',
  },
  eyebrowBrand: {
    textTransform: 'none',
  },
  title: {
    fontFamily: 'var(--font-display, inherit)',
    fontSize: '22px',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
    color: 'var(--text-primary)',
    margin: 0,
  },
  subtitle: {
    marginTop: '6px',
    fontSize: '13.5px',
    lineHeight: 1.5,
    color: 'var(--text-secondary)',
  },
  content: {
    padding: '18px 28px 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxHeight: 'min(60vh, 480px)',
    overflowY: 'auto',
  },
  kitCard: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'center',
    gap: '4px 16px',
    padding: '14px 16px',
    borderRadius: '12px',
    border: '1px solid var(--border-subtle)',
    background: 'var(--bg-raised)',
  },
  kitCardFeatured: {
    border: `1px solid ${tokens.colorBrandStroke1}`,
    boxShadow: 'var(--glow-brand)',
  },
  kitLabel: {
    fontSize: '14px',
    fontWeight: 700,
    color: 'var(--text-primary)',
  },
  kitTokens: {
    fontFamily: 'var(--font-display, inherit)',
    fontSize: '20px',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    color: tokens.colorBrandForeground1,
  },
  kitEq: {
    margin: 0,
    gridColumn: '1 / -1',
    fontSize: '12px',
    lineHeight: 1.45,
    color: 'var(--text-tertiary)',
  },
  kitBuyRow: {
    gridColumn: '1 / -1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    marginTop: '6px',
  },
  kitPrice: {
    fontFamily: 'var(--font-display, inherit)',
    fontSize: '18px',
    fontWeight: 700,
    color: 'var(--text-primary)',
  },
  footnote: {
    fontSize: '12px',
    lineHeight: 1.45,
    color: 'var(--text-tertiary)',
    padding: '2px 0 8px',
  },
  empty: { fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.5 },
  err: { color: 'var(--bad)', fontSize: '13px' },
  opened: {
    margin: 0,
    padding: '10px 12px',
    borderRadius: '10px',
    background: 'var(--ok-weak)',
    border: '1px solid var(--ok)',
    color: 'var(--text-primary)',
    fontSize: '13px',
    lineHeight: 1.45,
  },
  panteon: {
    border: '1px dashed var(--border-default)',
  },
  panteonLabel: {
    gridColumn: '1 / -1',
    fontSize: '12.5px',
    color: 'var(--text-secondary)',
  },
  panteonInput: {
    gridColumn: '1 / -1',
    height: '40px',
    padding: '0 12px',
    fontSize: '16px',
    fontVariantNumeric: 'tabular-nums',
    color: 'var(--text-primary)',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-control)',
    ':focus-visible': { outline: '2px solid var(--accent)', outlineOffset: '1px' },
  },
  actions: {
    padding: '12px 28px 20px',
    borderTop: '1px solid var(--border-subtle)',
    background: 'var(--bg-raised)',
  },
});

const fmtTokensCompact = new Intl.NumberFormat('es-MX', {
  notation: 'compact',
  maximumFractionDigits: 0,
});
const fmtN = new Intl.NumberFormat('es-MX');

function fmtPrice(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: currency.toUpperCase(),
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `$${amount.toLocaleString('es-MX')} ${currency.toUpperCase()}`;
  }
}

interface KitItem {
  id: string;
  label: string;
  tokens: number;
  creditos?: number;
  amount: number | null;
  currency: string;
}

/** Nombres de las recargas con créditos (el catálogo puede traer otro label; mandan estos). */
const NOMBRE_KIT: Record<string, string> = {
  kit_3m: 'Recarga 400',
  kit_1000cr: 'Recarga 1,000',
  kit_2500cr: 'Recarga 2,500',
  kit_5000cr: 'Recarga 5,000',
};
const KITS_CREDITOS = ['kit_3m', 'kit_1000cr', 'kit_2500cr', 'kit_5000cr'];

const FALLBACK_KITS: KitItem[] = [
  { id: 'kit_3m', label: 'Recarga 3M', tokens: 3_000_000, amount: 599, currency: 'mxn' },
];
/** Respaldo con créditos: solo la Recarga 400 (precio aprobado); las demás vienen de eko27. */
const FALLBACK_KITS_CREDITOS: KitItem[] = [
  { id: 'kit_3m', label: 'Recarga 400', tokens: 4_000_000, creditos: 400, amount: 599, currency: 'mxn' },
];

function toKit(p: StorePack | KitItem, cr: boolean): KitItem | null {
  if (!p.id || !(p.tokens > 0) || p.id === 'reload_ai') return null;
  if (cr ? !KITS_CREDITOS.includes(p.id) : p.id !== 'kit_3m') return null;
  return {
    id: p.id,
    label: cr ? (NOMBRE_KIT[p.id] ?? p.label ?? p.id) : (p.label || p.id),
    tokens: p.tokens,
    creditos: p.creditos,
    amount: p.amount,
    currency: p.currency || 'mxn',
  };
}

export function TiendaDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const s = useStyles();
  const usage = useUsageStore((st) => st.data);
  const [fetched, setFetched] = useState<StorePack[] | null>(null);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [buying, setBuying] = useState<string | null>(null);
  const [opened, setOpened] = useState(false);

  const cr = enCreditos(usage);
  const [panteonTxt, setPanteonTxt] = useState('1000');
  const [fetchedPanteon, setFetchedPanteon] = useState<ReturnType<typeof panteonDe> | null>(null);
  const storeKits = (usage?.kits ?? []).map((k) => toKit(k, cr)).filter((x): x is KitItem => !!x);
  const hasStoreKits = storeKits.length > 0;
  const promedios = useMemo(() => promediosDe(usage), [usage]);
  const panteon = fetchedPanteon ?? panteonDe(usage);

  useEffect(() => {
    if (!open) return;
    setErr(null);
    setOpened(false);
    if (hasStoreKits) return;
    setLoading(true);
    getStorePacks()
      .then((r) => {
        setAvailable(r.available);
        setFetched(r.packs);
        if (r.panteon) setFetchedPanteon(panteonDe({ panteon: r.panteon }));
      })
      .catch((e) => setErr((e as Error).message))
      .finally(() => setLoading(false));
  }, [open, hasStoreKits]);

  const fromFetch = (fetched ?? []).map((k) => toKit(k, cr)).filter((x): x is KitItem => !!x);
  const kits: KitItem[] = hasStoreKits
    ? storeKits
    : (fromFetch.length ? fromFetch : (!loading && !err ? (cr ? FALLBACK_KITS_CREDITOS : FALLBACK_KITS) : []));

  // Recarga a la medida: vista previa en vivo. El pago recalcula el precio en eko27.
  const panteonCr = Number(panteonTxt);
  const panteonOk = panteonValido(panteonCr, panteon);
  const panteonTotal = panteonOk ? precioPanteon(panteonCr, panteon) : null;
  const panteonRate = panteonOk ? precioCreditoPanteon(panteonCr, panteon) : null;
  const sugerencia =
    panteonTotal != null
      ? sugerirPaquete(
          panteonCr,
          panteonTotal,
          kits.map((k) => ({ id: k.id, label: k.label, creditos: creditosDeKit(k), amount: k.amount })),
        )
      : null;
  const panteonErr = !panteonTxt
    ? ''
    : panteonOk
      ? ''
      : `Escribe entre ${fmtN.format(panteon.min)} y ${fmtN.format(panteon.max)} créditos, en múltiplos de ${panteon.paso}.`;

  async function onBuy(kit: KitItem, creditos?: number) {
    setBuying(kit.id);
    setErr(null);
    try {
      const { url } = await createStoreCheckout(kit.id, creditos);
      window.open(url, '_blank', 'noopener');
      setOpened(true);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBuying(null);
    }
  }

  function handleClose() {
    if (opened) refreshUsageSoon();
    onClose();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(_, d) => {
        if (!d.open) handleClose();
      }}
    >
      <DialogSurface className={s.surface}>
        <DialogBody className={s.body}>
          <div className={s.hero}>
            <div className={s.eyebrow}>{nombreProductoActivo()} · Tienda</div>
            <DialogTitle as="h2" className={s.title}>
              {cr ? 'Recarga de créditos' : 'Recarga de tokens'}
            </DialogTitle>
            <p className={s.subtitle}>
              {cr
                ? 'Primero se gasta la bolsa mensual de tu plan; luego tus créditos de recarga. Las recargas no vencen.'
                : 'Primero se gasta la bolsa mensual de tu plan; luego los tokens de recarga. La recarga no se pierde al cambiar de mes y no vence.'}
            </p>
          </div>

          <DialogContent className={s.content}>
            {loading && <Spinner size="small" label="Cargando…" />}
            {err && <p className={s.err}>{err}</p>}
            {!hasStoreKits && fetched !== null && !available && kits.length === 0 && (
              <p className={s.empty}>La tienda no está disponible por ahora. Intenta más tarde o contacta a soporte.</p>
            )}

            {kits.map((kit) => (
              <div
                key={kit.id}
                className={mergeClasses(s.kitCard, s.kitCardFeatured)}
              >
                <span className={s.kitLabel}>{kit.label}</span>
                {cr ? (
                  <span className={s.kitTokens}>{etiquetaCreditos(kit.tokens)}</span>
                ) : (
                  <span className={s.kitTokens} title={`${fmtN.format(kit.tokens)} tokens`}>
                    {fmtTokensCompact.format(kit.tokens)} tokens
                  </span>
                )}
                <span className={s.kitEq}>
                  {cr
                    ? `${formatTokens(kit.tokens)} tokens · ${kitEquivalenceCreditos(creditosDeKit(kit), promedios, (n) => fmtN.format(n))}`
                    : kitEquivalenceLine(kit.tokens, (n) => fmtN.format(n))}
                </span>
                <div className={s.kitBuyRow}>
                  <span className={s.kitPrice}>
                    {kit.amount != null ? fmtPrice(kit.amount, kit.currency) : '—'}
                  </span>
                  <Button
                    appearance="primary"
                    disabled={kit.amount == null || buying !== null || (!available && !hasStoreKits)}
                    onClick={() => void onBuy(kit)}
                  >
                    {buying === kit.id ? 'Abriendo Stripe…' : 'Comprar'}
                  </Button>
                </div>
              </div>
            ))}

            {cr && (
              <div className={mergeClasses(s.kitCard, s.panteon)} data-testid="panteon">
                <span className={s.kitLabel}>
                  {/medida/i.test(panteon.nombre) ? panteon.nombre : `${panteon.nombre} · a la medida`}
                </span>
                <span className={s.kitTokens}>
                  {panteonOk ? etiquetaCreditos(panteonCr * 10_000) : '—'}
                </span>
                <label className={s.panteonLabel} htmlFor="panteon-creditos">
                  Créditos que quieres ({fmtN.format(panteon.min)} a {fmtN.format(panteon.max)}, de {panteon.paso} en {panteon.paso})
                </label>
                <input
                  id="panteon-creditos"
                  className={s.panteonInput}
                  type="number"
                  inputMode="numeric"
                  min={panteon.min}
                  max={panteon.max}
                  step={panteon.paso}
                  value={panteonTxt}
                  aria-invalid={panteonErr ? true : undefined}
                  aria-describedby="panteon-ayuda"
                  onChange={(e) => setPanteonTxt(e.target.value)}
                />
                <p id="panteon-ayuda" className={s.kitEq} aria-live="polite">
                  {panteonErr ||
                    (panteonTotal != null && panteonRate != null
                      ? `${formatTokens(panteonCr * 10_000)} tokens · $${panteonRate.toFixed(2)} por crédito en este tramo. ` +
                        kitEquivalenceCreditos(panteonCr, promedios, (n) => fmtN.format(n))
                      : '')}
                  {!panteonErr && sugerencia && sugerencia.creditos > panteonCr
                    ? ` Por ${fmtPrice(sugerencia.amount, 'mxn')} llévate ${/^recarga\s/i.test(sugerencia.label) ? 'la ' : ''}${sugerencia.label} (${etiquetaCreditos(sugerencia.creditos * 10_000)}).`
                    : ''}
                </p>
                <div className={s.kitBuyRow}>
                  <span className={s.kitPrice} data-testid="panteon-precio">
                    {panteonTotal != null ? fmtPrice(panteonTotal, 'mxn') : '—'}
                  </span>
                  <Button
                    appearance="primary"
                    disabled={panteonTotal == null || buying !== null}
                    onClick={() =>
                      void onBuy({ id: panteon.id, label: panteon.nombre, tokens: panteonCr * 10_000, amount: panteonTotal, currency: 'mxn' }, panteonCr)
                    }
                  >
                    {buying === panteon.id ? 'Abriendo Stripe…' : 'Comprar'}
                  </Button>
                </div>
              </div>
            )}

            {opened && (
              <p className={s.opened}>
                El pago se abrió en otra pestaña. Al completarlo, {cr ? 'tus créditos' : 'tus tokens'} se acreditan en unos segundos.
              </p>
            )}

            <p className={s.footnote}>
              Orden de consumo: 1) bolsa mensual del plan → 2) recargas. Las recargas no se pierden al cambiar de mes y no vencen.
              {cr ? ` ${NOTA_EQUIVALENCIA}. Precios con IVA incluido.` : ''}
            </p>
          </DialogContent>

          <DialogActions className={s.actions}>
            <Button appearance="secondary" onClick={handleClose}>
              Cerrar
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
